// 通常業務 / 計画業務ページで共通利用する「選択中の工事ID」永続化フック。
// localStorage 単一キーで保持し、ページ間 / ブラウザ再起動を跨いで永続化。
// 同一タブ内の別コンポーネント間でも即時同期する(カスタムイベント経由)。

import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "okumura-selected-work-id-v1";
const EVENT_NAME = "okumura-selected-work-changed";

function readStored(): number | undefined {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === null || v === "") return undefined;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function writeStored(v: number | undefined) {
  try {
    if (v === undefined) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(v));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: v }));
}

// 工事ID(number)を永続化付きで保持する。
// 戻り値の workId は number | ''(MUI Select の互換のため空文字許容)。
export function useSelectedWork(): [number | "", (v: number | "") => void] {
  const [workId, setWorkIdState] = useState<number | "">(() => readStored() ?? "");

  useEffect(() => {
    const onChange = (e: Event) => {
      if (!(e instanceof CustomEvent)) {
        return;
      }
      const detail: unknown = e.detail;
      setWorkIdState(typeof detail === "number" ? detail : "");
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setWorkIdState(readStored() ?? "");
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setWorkId = useCallback((v: number | "") => {
    setWorkIdState(v);
    writeStored(v === "" ? undefined : v);
  }, []);

  return [workId, setWorkId];
}

// 永続化された workId と有効工事一覧から、実際に表示する workId を決める。
export function resolveWorkId(persistedWorkId: number | "", activeWorks: { id: number }[]): number | "" {
  if (persistedWorkId !== "" && activeWorks.some((w) => w.id === persistedWorkId)) {
    return persistedWorkId;
  }
  const firstWork = activeWorks[0];
  if (firstWork !== undefined) {
    return firstWork.id;
  }
  return "";
}
